import { useCallback, useState } from "react";
import { listProjectTestRuns, startProjectTestRun, type QaTestRunSummary, type QaTrigger } from "../api/qaOrchestrator";
import { relativeTime } from "./admin/memberModel";
import { describeTarget } from "../lib/qaTarget";
import { formatDuration } from "../lib/qaTimeline";
import { usePolling } from "../hooks/usePolling";
import "./admin/qa-orchestrator.css";
import "./admin/qa-run.css";

type Props = { project: string; onOpenRun: (id: string) => void };

export const OPEN = new Set(["pending", "running"]);

/** "PR #12 Add the ruby backpack", "push", "manual"... */
export function triggerLabel(t: QaTrigger | null): string | null {
  if (!t) return null;
  if (t.prNumber) return `PR #${t.prNumber}${t.prTitle ? ` ${t.prTitle}` : ""}`;
  return t.event.replace(/_/g, " ");
}

/** The run's headline: what started it, else the ref it tested. */
export function runTitle(r: QaTestRunSummary): string {
  return triggerLabel(r.trigger) ?? (r.ref !== r.sha ? r.ref : `commit ${r.sha.slice(0, 8)}`);
}

export function CountChips({ counts }: { counts: Record<string, number> }) {
  return (
    <span className="qr-counts">
      {Object.entries(counts).map(([k, v]) => <span key={k} className={`qr-count qr-count-${k}`}>{v} {k}</span>)}
    </span>
  );
}

/** How long a finished run took from start to its last plan's verdict, e.g. "14m 32s"; null while it runs. */
export function runDuration(run: Pick<QaTestRunSummary, "createdAt" | "finishedAt">): string | null {
  if (!run.finishedAt) return null;
  const ms = Date.parse(run.finishedAt) - Date.parse(run.createdAt);
  return Number.isFinite(ms) && ms >= 0 ? formatDuration(ms) : null;
}

function RunRow({ summary, onOpen }: { summary: QaTestRunSummary; onOpen: () => void }) {
  const open = OPEN.has(summary.status);
  return (
    <button className={`qo-run-item qo-run-item-${summary.status}`} onClick={onOpen}>
      <span className={`qo-pill qo-pill-${summary.status}`}>{open && <span className="qr-live-dot" />}{summary.status}</span>
      <span className="qo-run-main">
        <span className="qo-run-title">{runTitle(summary)}</span>
        <span className="qo-muted qo-run-meta">
          <code>{summary.sha.slice(0, 8)}</code>
          {summary.ref !== summary.sha && triggerLabel(summary.trigger) ? ` · ${summary.ref}` : ""}
          {` · ${summary.plans.length} plan${summary.plans.length === 1 ? "" : "s"}`}
          {summary.packs.length > 0 ? " · PR build" : ""}
          {summary.kind === "capture" ? " · re-baseline" : ""}
          {` · ${describeTarget(summary.target)}`}
          {runDuration(summary) ? ` · took ${runDuration(summary)}` : ""}
        </span>
      </span>
      <CountChips counts={summary.counts} />
      <span className="qo-muted qo-run-when">{relativeTime(summary.createdAt)}</span>
      <span className="qo-run-chevron" aria-hidden="true">›</span>
    </button>
  );
}

/**
 * The project's test runs on the qa-agent orchestrator: every run of its repo,
 * from CI or started here, newest first. A run opens on its own page. Shows a
 * hint when the daemon has no orchestrator configured.
 */
export default function QaTestRuns({ project, onOpenRun }: Props) {
  const [data, setData] = useState<Awaited<ReturnType<typeof listProjectTestRuns>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ref, setRef] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => listProjectTestRuns(project).then((d) => { setData(d); setError(d.error ?? null); }).catch((e) => setError((e as Error).message)), [project]);
  // Refreshes by itself: often while a run is going, now and then otherwise.
  const anyOpen = data?.testRuns.some((r) => OPEN.has(r.status)) ?? false;
  usePolling(load, anyOpen ? 5_000 : 20_000);

  if (data && !data.configured) return <p className="qo-hint">QA testing is not set up yet. An admin connects it under Manage → QA.</p>;
  return (
    <section className="qo-runs">
      <div className="qo-runs-head">
        <h2>Test runs{data?.repo ? <span className="qo-muted"> · {data.repo}</span> : null}</h2>
        <input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="branch, tag or commit" aria-label="Ref to test" />
        <button className="btn" disabled={busy} title="Runs the plans in qa/plans at that ref, with the pack versions the runners have. Pull requests are tested with their own build by CI." onClick={async () => {
          setBusy(true);
          try {
            const { testRun } = await startProjectTestRun(project, ref.trim() || undefined);
            setRef("");
            await load();
            onOpenRun(testRun.id);
          } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
        }}>Run tests</button>
      </div>
      {error && <div className="qo-error-text" role="alert">{error}</div>}
      {!data ? (
        <div className="qo-muted">Loading test runs…</div>
      ) : data.testRuns.length === 0 ? (
        <div className="qo-muted">No test runs yet. They appear here when CI tests a pull request, or when you run them.</div>
      ) : (
        <div className="qo-run-list">
          {data.testRuns.map((r) => <RunRow key={r.id} summary={r} onOpen={() => onOpenRun(r.id)} />)}
        </div>
      )}
    </section>
  );
}
